// Version 3: zentrale Seitenliste (Vertrag für Menü, Routen, Versionsumschalter, SEO, Sitemap und Prüfwerkzeug).
// Reines ESM ohne JSX und ohne Alias, weil tools/qa/qa.py diese Datei über Node lädt.
// Stand 22.10.2026: Sitemap nach dem Briefing der Geschäftsführung (Solutions, Produkte und Anlagen, Wissen, Unternehmen,
// dazu vier Landingpages für Suche und Ads). Alles auf Deutsch, nur „Solutions“ bleibt.
// Jede Seite: path, template, menu (Hauptpunkt), group (Spalte im Mega Menü), label de/en, built (fertig gebaut)
// und optional lead: { intent, situation, way } zum Vorbelegen des Formulars auf /kontakt
// sowie sections: Kategorien einer Produkte-Übersicht (eigene Seiten unter `${path}/${section.id}`).
// Englische Bezeichnungen sind vorerst Platzhalter in eckigen Klammern.

const EN = (s) => `[${s}]`;

// Hauptpunkte und Spalten des Mega Menüs, in Anzeigereihenfolge
export const V3_SECTIONS = [
    {
        id: "solutions", to: "/solutions", de: "Solutions", en: "Solutions",
        groups: [
            { id: "leistungen", de: "Unsere Leistungen", en: EN("Our solutions") },
            { id: "herausforderung", de: "Ihre Herausforderung", en: EN("By challenge") },
            { id: "rolle", de: "Für Ihre Rolle", en: EN("By role") },
        ],
    },
    {
        id: "produkte", to: "/produkte-und-anlagen", de: "Produkte und Anlagen", en: "Industries & Assets",
        // Spalten zeigen die Kategorieseiten der jeweiligen Übersicht (nicht mehr Anker)
        groups: [
            { id: "komponenten", de: "Komponenten und Automation", en: EN("Industrial components & automation"), page: "/produkte-und-anlagen/komponenten-automation" },
            { id: "maschinen", de: "Maschinen und Anlagen", en: EN("Machinery & equipment"), page: "/produkte-und-anlagen/maschinen-anlagen" },
        ],
    },
    {
        id: "wissen", to: "/wissen", de: "Wissen", en: "Insights",
        groups: [{ id: "wissen", de: "Wissen", en: EN("Insights") }],
    },
    {
        id: "unternehmen", to: "/unternehmen", de: "Unternehmen", en: "Company",
        groups: [{ id: "unternehmen", de: "Unternehmen", en: EN("Company") }],
    },
];

const sale = (situation = "") => ({ intent: "assets", situation, way: "sale" });
const S = (id, de, en) => ({ id, de, en: EN(en) });

/** Kategorieseite unter einer Produkte-Übersicht (eigenes Formular). */
const cat = (parent, group, s) => ({
    path: `${parent}/${s.id}`,
    template: "productCategory",
    menu: "produkte",
    group,
    parent,
    section: s.id,
    label: { de: s.de, en: s.en },
    lead: sale(),
    built: true,
});

const KOMPONENTEN = "/produkte-und-anlagen/komponenten-automation";
const MASCHINEN = "/produkte-und-anlagen/maschinen-anlagen";

const KOMPONENTEN_SECTIONS = [
    S("sps-steuerungen", "SPS und Steuerungen", "PLC & controls"),
    S("frequenzumrichter", "Frequenzumrichter", "Frequency inverters"),
    S("servomotoren-antriebstechnik", "Servomotoren und Antriebstechnik", "Servo motors & drives"),
    S("hmi-industrieelektronik", "HMI und Industrieelektronik", "HMI & industrial electronics"),
    S("pneumatik", "Pneumatik", "Pneumatics"),
    S("weitere-komponenten", "Weitere Komponenten", "Other components"),
];

const MASCHINEN_SECTIONS = [
    S("metallbearbeitung", "Metallbearbeitungsmaschinen", "Metalworking machines"),
    S("kunststoffmaschinen", "Kunststoffmaschinen", "Plastics machines"),
    S("holzbearbeitung", "Holzbearbeitungsmaschinen", "Woodworking machines"),
    S("produktionsanlagen", "Produktionsanlagen", "Production lines"),
    S("maschinenersatzteile", "Maschinenersatzteile", "Machine spare parts"),
];

// template: home | overview | solution | challenge | role | products | productCategory | landing | knowledge | company | contact | legal
export const V3_PAGE_LIST = [
    { path: "/", template: "home", label: { de: "Startseite", en: "Home" }, built: true },

    // Solutions
    { path: "/solutions", template: "overview", menu: "solutions", label: { de: "Solutions", en: "Solutions" }, built: true },
    { path: "/solutions/bestandsanalyse-bewertung", template: "solution", menu: "solutions", group: "leistungen", label: { de: "Bestandsanalyse und Bewertung", en: EN("Asset intelligence") }, lead: sale(), built: true },
    { path: "/solutions/abwicklung-logistik", template: "solution", menu: "solutions", group: "leistungen", label: { de: "Abwicklung und Logistik", en: EN("Asset operations") }, lead: sale(), built: true },
    { path: "/solutions/marktzugang", template: "solution", menu: "solutions", group: "leistungen", label: { de: "Marktzugang", en: EN("Market execution") }, lead: sale(), built: true },
    { path: "/solutions/verwertung-rohstoffrueckgewinnung", template: "solution", menu: "solutions", group: "leistungen", label: { de: "Verwertung und Rohstoffrückgewinnung", en: EN("Responsible resource use") }, lead: sale(), built: true },
    { path: "/solutions/ueberbestaende", template: "challenge", menu: "solutions", group: "herausforderung", label: { de: "Überbestände", en: EN("Surplus & excess inventory") }, lead: sale("surplus"), built: true },
    { path: "/solutions/altbestaende-langsamdreher", template: "challenge", menu: "solutions", group: "herausforderung", label: { de: "Altbestände und Langsamdreher", en: EN("Obsolete & slow moving") }, lead: sale("obsolete"), built: true },
    { path: "/solutions/ersatzteile-instandhaltung", template: "challenge", menu: "solutions", group: "herausforderung", label: { de: "Ersatzteile und Instandhaltung", en: EN("MRO & spare parts") }, lead: sale("mro"), built: true },
    { path: "/solutions/werksschliessung-restrukturierung", template: "challenge", menu: "solutions", group: "herausforderung", label: { de: "Werksschließung und Restrukturierung", en: EN("Plant closures & restructuring") }, lead: sale("closure"), built: true },
    { path: "/solutions/betrieb-geschaeftsfuehrung", template: "role", menu: "solutions", group: "rolle", label: { de: "Betrieb und Geschäftsführung", en: EN("Operations & management") }, lead: sale(), built: true },
    { path: "/solutions/instandhaltung", template: "role", menu: "solutions", group: "rolle", label: { de: "Instandhaltung", en: EN("Maintenance & MRO") }, lead: sale(), built: true },
    { path: "/solutions/einkauf-logistik", template: "role", menu: "solutions", group: "rolle", label: { de: "Einkauf und Logistik", en: EN("Supply chain & procurement") }, lead: sale(), built: true },
    { path: "/solutions/finanzen-controlling", template: "role", menu: "solutions", group: "rolle", label: { de: "Finanzen und Controlling", en: EN("Finance & controlling") }, lead: sale(), built: true },

    // Produkte und Anlagen (Übersichten + eigene Kategorieseiten mit Formular)
    { path: "/produkte-und-anlagen", template: "overview", menu: "produkte", label: { de: "Produkte und Anlagen", en: "Industries & Assets" }, built: true },
    {
        path: KOMPONENTEN, template: "products", menu: "produkte", group: "komponenten",
        label: { de: "Komponenten und Automation", en: EN("Industrial components & automation") }, lead: sale(), built: true,
        sections: KOMPONENTEN_SECTIONS,
    },
    ...KOMPONENTEN_SECTIONS.map((s) => cat(KOMPONENTEN, "komponenten", s)),
    {
        path: MASCHINEN, template: "products", menu: "produkte", group: "maschinen",
        label: { de: "Maschinen und Anlagen", en: EN("Machinery & equipment") }, lead: sale(), built: true,
        sections: MASCHINEN_SECTIONS,
    },
    ...MASCHINEN_SECTIONS.map((s) => cat(MASCHINEN, "maschinen", s)),

    // Landingpages für Suche und Ads (nicht im Mega Menü; verlinkt aus Produkte und Anlagen, Startseite und Fußzeile)
    { path: "/bestaende-verkaufen", template: "landing", group: "landing", label: { de: "Industrielle Bestände verkaufen und verwerten", en: EN("Sell industrial inventory") }, lead: sale(), built: true },
    { path: "/ueberbestaende-verkaufen", template: "landing", group: "landing", label: { de: "Überbestände verkaufen", en: EN("Sell surplus stock") }, lead: sale("surplus"), built: true },
    { path: "/maschinen-anlagen-verkaufen", template: "landing", group: "landing", label: { de: "Maschinen und Anlagen verkaufen", en: EN("Sell machinery and equipment") }, lead: sale(), built: true },
    { path: "/standortschliessung", template: "landing", group: "landing", label: { de: "Standortschließung und Werksräumung", en: EN("Site and plant closures") }, lead: sale("closure"), built: true },

    // Wissen
    { path: "/wissen", template: "overview", menu: "wissen", label: { de: "Wissen", en: "Insights" }, built: true },
    { path: "/wissen/referenzen", template: "knowledge", menu: "wissen", group: "wissen", label: { de: "Referenzen", en: EN("References") }, built: true },
    { path: "/wissen/praxisbeispiele", template: "knowledge", menu: "wissen", group: "wissen", label: { de: "Praxisbeispiele", en: EN("Case studies") }, built: true },
    { path: "/wissen/fachartikel", template: "knowledge", menu: "wissen", group: "wissen", label: { de: "Fachartikel", en: EN("Articles") }, built: true },
    { path: "/wissen/news-presse", template: "knowledge", menu: "wissen", group: "wissen", label: { de: "Neuigkeiten und Presse", en: EN("News & press") }, built: true },

    // Unternehmen
    { path: "/unternehmen", template: "overview", menu: "unternehmen", label: { de: "Unternehmen", en: "Company" }, built: true },
    { path: "/unternehmen/ueber-parto", template: "company", menu: "unternehmen", group: "unternehmen", label: { de: "Über Parto", en: EN("About Parto") }, built: true },
    { path: "/unternehmen/netzwerk", template: "company", menu: "unternehmen", group: "unternehmen", label: { de: "Netzwerk", en: EN("Ecosystem") }, built: true },
    { path: "/unternehmen/partner", template: "company", menu: "unternehmen", group: "unternehmen", label: { de: "Partner", en: EN("Partners") }, built: true },
    { path: "/unternehmen/nachhaltigkeit", template: "company", menu: "unternehmen", group: "unternehmen", label: { de: "Nachhaltigkeit", en: EN("Sustainability & circularity") }, built: true },
    { path: "/unternehmen/karriere", template: "company", menu: "unternehmen", group: "unternehmen", label: { de: "Karriere", en: EN("Careers") }, built: true },

    // Anfrage (steht im Menü unter Unternehmen als „Kontakt“)
    { path: "/kontakt", template: "contact", menu: "unternehmen", group: "unternehmen", label: { de: "Kontakt", en: EN("Contact") }, built: true },

    // Rechtliches (bestehende Seiten)
    { path: "/impressum", template: "legal", label: { de: "Impressum", en: "Imprint" }, built: true },
    { path: "/datenschutz", template: "legal", label: { de: "Datenschutz", en: "Privacy" }, built: true },
    { path: "/llm-info", template: "legal", label: { de: "Informationen für KI", en: "LLM info" }, built: true },
];

// Alle gültigen Pfade von Version 3 (für Links, Versionsumschalter und Prüfwerkzeug)
export const V3_PAGES = V3_PAGE_LIST.map((p) => p.path);

export const v3Page = (path) => V3_PAGE_LIST.find((p) => p.path === path) || null;
